<template>
  <a-modal
    v-model:visible="visible"
    title="完成工单"
    width="500px"
    @ok="handleSubmit"
    ok-text="提交并完成"
    cancel-text="取消"
  >
    <a-form ref="formRef" :model="formState" :rules="rules" layout="vertical">
      <div class="form-container">
        <!-- 患者期望时间 -->
        <div class="time-section">患者期望时间： {{ getMomentTime() || '--' }}</div>
        <!-- 完成方式 -->
        <a-form-item label="完成方式" name="completionStatus">
          <a-radio-group
            v-model:value="formState.completionStatus"
            @change="handleCompletionMethodChange"
          >
            <a-radio v-for="item in completionMethodOptions" :key="item.value" :value="item.value">
              <span class="radio-label">{{ item.label }}</span>
            </a-radio>
          </a-radio-group>
        </a-form-item>
        <!-- 医生已咨询时间（仅在选择"协助医生确认时间"时显示） -->
        <div v-if="formState.completionStatus === 1" class="doctor-time-section">
          <div class="doctor-time-list" v-if="info?.confirmedConsults?.length">
            <div class="section-title">医生已约咨询时间</div>
            <div
              v-for="(item, index) in info?.confirmedConsults"
              :key="index"
              class="doctor-time-item"
            >
              <div class="time-label">{{ moment(item.date).format('MM月DD日') }}：</div>
              <div class="list-container">
                <template v-if="item?.consultReservations.length">
                  <span
                    v-for="(pItems, pIndex) in item.consultReservations"
                    :key="pIndex"
                    class="doctor-time-item-list"
                  >
                    {{ pItems.patientName }}（{{ getTime(pItems) }}）
                  </span>
                </template>
                <template v-else>
                  <span class="doctor-time-item-list">--</span>
                </template>
              </div>
            </div>
            <div class="tips" v-if="doctorRemark">医生咨询时间备注：{{ doctorRemark }}</div>
          </div>
        </div>

        <!-- 选择协助医生确认时间 -->
        <div v-if="formState.completionStatus === 1" class="consult-time-section">
          <a-form-item label="选择咨询开始时间" name="confirmTime">
            <el-date-picker
              v-model="formState.confirmTime"
              type="datetime"
              value-format="YYYY-MM-DD HH:mm:ss"
              format="YYYY-MM-DD HH:mm"
              placeholder="选择时间"
              :disabled-date="disabledDate"
            ></el-date-picker>
          </a-form-item>
        </div>

        <!-- 选择已知晓 -->
        <div v-if="formState.completionStatus === 2" class="supplement-section">
          <a-form-item label="补充说明" name="additionalNotes">
            <a-textarea
              v-model:value="formState.additionalNotes"
              placeholder="请输入补充说明内容..."
              :rows="3"
              maxLength="100"
              show-count
            />
          </a-form-item>
        </div>

        <!-- 选择无法联系医生 -->
        <div v-if="formState.completionStatus === 3" class="supplement-section">
          <a-form-item label="补充说明" name="additional">
            <a-textarea
              v-model:value="formState.additional"
              placeholder="请输入补充说明内容..."
              maxLength="100"
              :rows="3"
              show-count
            />
          </a-form-item>
        </div>
      </div>
    </a-form>
  </a-modal>
</template>

<script setup>
import {
  getConsultInfo,
  confirmConsultTime,
  getConsultInfoRemark,
  getConsultIdByWorkOrderId
} from '@/api/workPlace/consult'
import moment from 'moment'
import { useStore } from 'vuex'
import { ref, reactive, computed, defineEmits, defineExpose, watch } from 'vue'
import { validateDate, disabledDate } from './config'
import { message, Modal } from 'ant-design-vue'
const emit = defineEmits(['saveSuccess'])
const store = useStore()

// 完成方式选项
// 1协助医生确认时间 2确认医生已知晓 3无法触达医生
const completionMethodOptions = [
  {
    label: '协助医生确认时间',
    value: 1
  },
  {
    label: '确认医生已知晓',
    value: 2
  },
  {
    label: '无法触达医生',
    value: 3
  }
]
// 表单引用和状态管理
const formRef = ref(null)
const visible = ref(false)
const formState = reactive({
  completionStatus: '',
  confirmTime: '',
  additionalNotes: '',
  additional: ''
})
const info = ref({})
const consultId = ref(null)
const doctorRemark = ref('')
watch(
  () => visible.value,
  async val => {
    if (!val) {
      formRef.value?.resetFields()
    }
  }
)

const initData = async workOrderId => {
  try {
    consultId.value = await getConsultIdByWorkOrderId({ workOrderId })
    if (!consultId.value) return message.error('没有咨询ID')
    visible.value = true
    // 获取数据
    const res = await getConsultInfo({ consultId: consultId.value })

    // 获取医生咨询时间备注
    const remark = await getConsultInfoRemark({
      doctorUcId: res.ucDoctorId,
      consultType: res.consultType
    })
    doctorRemark.value = remark
    info.value = res
  } catch (error) {
    console.log('初始化失败：', error)
  }
}
const getTime = item => {
  const { confirmStartTime, confirmEndTime } = item || {}
  // 判断confirmStartTime和confirmEndTime 是否是同一天
  if (moment(confirmStartTime).format('YYMMDD') !== moment(confirmEndTime).format('YYMMDD')) {
    const startTime = moment(confirmStartTime).format('DD日 HH:mm')
    const endTime = moment(confirmEndTime).format('DD日 HH:mm')
    return `${startTime} - ${endTime}`
  } else {
    const startTime = moment(confirmStartTime).format('HH:mm')
    const endTime = moment(confirmEndTime).format('HH:mm')
    return `${startTime} - ${endTime}`
  }
}
const getMomentTime = () => {
  const { expectedStartTime, expectedEndTime } = info.value || {}
  if (!expectedStartTime || !expectedEndTime) {
    return ''
  }
  const expectTimeObj = {
    momentTime: moment(expectedStartTime).format('MM月DD日'),
    momentTimeStart: moment(expectedStartTime).format('HH:mm'),
    momentTimeEnd: moment(expectedEndTime).format('HH:mm')
  }
  return `${expectTimeObj.momentTime} ${expectTimeObj.momentTimeStart} - ${expectTimeObj.momentTimeEnd}`
}
// 处理提交
const handleSubmit = () => {
  if (!formRef.value) return
  const { completionStatus, confirmTime } = formState
  const { expectedStartTime, expectedEndTime } = info.value || {}
  //如果选择的 confirmTime 不在expectedStartTime到expectedEndTime的时间段内，则提示选择时间段内
  formRef.value
    .validate()
    .then(() => {
      const confirmTimeValue = moment(confirmTime).format('YYYY-MM-DD HH:mm:00')
      // 处理isBetween的边界值一样可以满足
      if (completionStatus !== 1) {
        handleFinish()
      } else {
        const confirmTimeIsInTime =
          moment(confirmTimeValue).isBetween(expectedStartTime, expectedEndTime) ||
          moment(confirmTimeValue).isSame(expectedStartTime) ||
          moment(confirmTimeValue).isSame(expectedEndTime)
        if (!confirmTimeIsInTime) {
          Modal.confirm({
            title: () => '',
            content: () => '你选择的时间不在患者期望时间内，确认选择该时间吗？',
            centered: true,
            onOk() {
              handleFinish()
            },
            onCancel() {
              console.log('Cancel')
            }
          })
        } else {
          handleFinish()
        }
      }
    })
    .catch(errorInfo => {
      console.error('表单验证失败：', errorInfo)
      // 表单验证失败，显示错误提示
    })
}

const handleFinish = () => {
  const { completionStatus, confirmTime, additionalNotes, additional } = formState
  confirmConsultTime({
    consultId: consultId.value,
    completionStatus, //完成情况
    confirmTime: completionStatus === 1 ? confirmTime : '',
    additionalNotes:
      completionStatus !== 1 ? (completionStatus === 2 ? additionalNotes : additional) : ''
  }).then(res => {
    // message.success('完成成功')
    visible.value = false
    emit('saveSuccess')
  })
}

// 动态校验规则
const rules = computed(() => {
  const baseRules = {
    completionStatus: [
      { required: true, message: '请选择完成方式', trigger: 'change', type: 'number' }
    ]
  }
  // 根据选择的完成方式添加对应的校验规则
  if (formState.completionStatus === 1) {
    baseRules.confirmTime = [
      {
        required: true,
        trigger: 'change',
        validator: validateDate
      }
    ]
  }
  return baseRules
})

// 处理完成方式变更
const handleCompletionMethodChange = () => {
  // 重置表单验证状态
  if (formRef.value) {
    formRef.value.clearValidate()
  }
}
defineExpose({ initData })
</script>

<style scoped lang="scss">
.form-container {
  // 患者期望时间样式
  .time-section {
    background: #ff992b14;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 24px;
    font-weight: 500;
    font-size: 16px;
    color: #ff992b;
  }

  // 单选按钮样式
  .radio-label {
    font-weight: 400;
    font-size: 14px;
    color: #3b3b3b;
  }
}

// 通用部分标题样式
.section-title {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 6px;
  color: #262626;
  display: flex;
  align-items: center;
}
.tips {
  font-size: 14px;
  color: #ff992b;
}
// 各个部分的间距
.doctor-time-section,
.consult-time-section,
.supplement-section {
  margin-bottom: 24px;
}

// 医生咨询时间列表样式
.doctor-time-list {
  background: #f7f7f7;
  padding: 12px 16px;
  border-radius: 8px;
}

// 单个医生时间项样式
.doctor-time-item {
  margin-bottom: 10px;
  line-height: 1.5;
  display: grid;
  grid-template-columns: auto 1fr;
  font-size: 14px;
  color: #333333;

  .time-label {
  }

  .list-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }

  .doctor-time-item-list {
  }

  &:last-child {
    margin-bottom: 0;
  }
}
</style>
; ; ; ;;
